import React, { createContext, useContext, useEffect, useMemo } from 'react';
import { ThemeProvider as MuiThemeProvider, Theme } from '@mui/material/styles';
import { Business, fallbackBusiness } from '../services/BusinessService';
import { BODY_FONT, themeToCssVars } from '../theme/businessTheme';

const Ctx = createContext<Business>(fallbackBusiness(''));

/** The business currently being rendered (defaults when outside a provider). */
export const useBusinessTheme = () => useContext(Ctx);

const FONT_LINK_ID = 'biz-fonts';
const FONT_HREF =
  'https://fonts.googleapis.com/css2?family=Bricolage+Grotesque:opsz,wght@12..96,400..800&family=DM+Sans:opsz,wght@9..40,400..700&display=swap';

interface Props {
  business: Business;
  /** Hide the 480px column limit (not used today). */
  children: React.ReactNode;
}

/**
 * Full-viewport branded shell. Exposes the §3 slot tokens as CSS variables
 * (on the wrapper AND on <html>, so portals such as dialogs inherit them),
 * overrides the MUI palette for reused components (payments, questionnaire),
 * and centers a max-480px column on desktop with the brand background
 * extending full-bleed.
 */
export const BusinessThemeProvider: React.FC<Props> = ({ business, children }) => {
  const t = business.theme;
  const vars = useMemo(() => themeToCssVars(t), [t]);

  useEffect(() => {
    if (!document.getElementById(FONT_LINK_ID)) {
      const link = document.createElement('link');
      link.id = FONT_LINK_ID;
      link.rel = 'stylesheet';
      link.href = FONT_HREF;
      document.head.appendChild(link);
    }
  }, []);

  useEffect(() => {
    const root = document.documentElement;
    const prevBg = document.body.style.backgroundColor;
    Object.entries(vars).forEach(([k, v]) => root.style.setProperty(k, v));
    document.body.style.backgroundColor = t.background;
    let meta = document.querySelector('meta[name="theme-color"]') as HTMLMetaElement | null;
    const prevMeta = meta?.content;
    if (!meta) {
      meta = document.createElement('meta');
      meta.name = 'theme-color';
      document.head.appendChild(meta);
    }
    meta.content = t.background.slice(0, 7);
    return () => {
      Object.keys(vars).forEach((k) => root.style.removeProperty(k));
      document.body.style.backgroundColor = prevBg;
      if (meta) {
        if (prevMeta !== undefined) meta.content = prevMeta;
        else meta.remove();
      }
    };
  }, [vars, t.background]);

  const muiTheme = useMemo(
    () => (outer: Theme): Theme => ({
      ...outer,
      palette: {
        ...outer.palette,
        mode: 'light',
        primary: { ...outer.palette.primary, main: t.action, contrastText: t.onAction },
        secondary: { ...outer.palette.secondary, main: t.accent },
        background: { default: t.surface, paper: t.card },
        text: { ...outer.palette.text, primary: t.onSurface, secondary: t.onSurfaceMuted },
        divider: t.cardBorder,
      },
      typography: { ...outer.typography, fontFamily: BODY_FONT },
    }),
    [t]
  );

  return (
    <Ctx.Provider value={business}>
      <MuiThemeProvider theme={muiTheme}>
        <div
          style={{
            ...(vars as React.CSSProperties),
            background: t.background,
            color: t.onBackground,
            height: '100dvh',
            width: '100%',
            overflow: 'hidden',
            fontFamily: BODY_FONT,
          }}
        >
          <div
            style={{
              position: 'relative',
              margin: '0 auto',
              maxWidth: 480,
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              background: t.surface,
              boxShadow: '0 0 0 1px rgba(0,0,0,0.04)',
            }}
          >
            {children}
          </div>
        </div>
      </MuiThemeProvider>
    </Ctx.Provider>
  );
};

export default BusinessThemeProvider;
